import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";

import { useSaveActionsContext } from "context/save";

import useSectionFourTextsApi from "hooks/api/private/blocks/useSectionFourTextsApi";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionFourTextsBody, SectionFourTextsDetails } from "types/api/blocks/sectionFourTexts";
import { LanguageEnum } from "types/api/commons";

export type SectionTitleHomepageForm = {
    id: string | undefined;
    text: {
        [x in LanguageEnum]: {
            text_one: string;
            text_two: string;
            text_three: string;
            text_four: string;
        };
    };
};

type SectionTitleHomepageTitleType = {
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionTitleHomepageForm>;
};

function useSectionTitleHomepage(): SectionTitleHomepageTitleType {
    const { blockGroupCompositionId, blockId, graphicalOrder, blockGroupId } =
        useBlockGroupContext();

    const { saveSectionFourTexts, getOneSectionFourTexts, deleteSectionFourTexts } =
        useSectionFourTextsApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                id: {
                    type: "simple",
                    validators: [],
                },
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            text_one: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            text_two: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            text_three: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                            text_four: {
                                type: "simple",
                                validators: [],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: SectionTitleHomepageForm = React.useMemo(() => {
        return {
            id: undefined,
            text: {
                it: {
                    text_one: "",
                    text_two: "",
                    text_three: "",
                    text_four: "",
                },
                en: {
                    text_one: "",
                    text_two: "",
                    text_three: "",
                    text_four: "",
                },
            },
        };
    }, []);

    const formHook = useForm<SectionTitleHomepageForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [sectionTitleHomepageDetails, setSectionTitleHomepageDetails] = React.useState<
        SectionFourTextsDetails | undefined
    >(undefined);

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionFourTexts(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionTitleHomepageDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionTitleHomepageDetails, getOneSectionFourTexts, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionTitleHomepageDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionTitleHomepageDetails.id;
                newForm.text = sectionTitleHomepageDetails.text;

                return newForm;
            });
        }
    }, [setForm, sectionTitleHomepageDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const sectionTitleHomepageBody: SectionFourTextsBody = {
                    id: blockGroupCompositionId,
                    text: {
                        it: {
                            text_one: form.text.it.text_one,
                            text_two: form.text.it.text_two,
                            text_three: form.text.it.text_three,
                            text_four: form.text.it.text_four,
                        },
                        en: {
                            text_one: form.text.en.text_one,
                            text_two: form.text.en.text_two,
                            text_three: form.text.en.text_three,
                            text_four: form.text.it.text_four,
                        },
                    },
                };

                const [apiCall] = saveSectionFourTexts(sectionTitleHomepageBody);

                const promises: Promise<unknown>[] = [];

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveSectionFourTexts,
        addSaveAction,
        showAllError,
        blockGroupCompositionId,
        isValid,
        form,
        blockGroupId,
        blockId,
        graphicalOrder,
    ]);

    return React.useMemo(
        (): SectionTitleHomepageTitleType => ({
            buttonDelete: deleteSectionFourTexts,
            formHook,
        }),
        [deleteSectionFourTexts, formHook],
    );
}

export default useSectionTitleHomepage;
